<template>
    <main class="d-flex flex-column justify-content-between bgray-100 mh-100 vh-100">
        <b-navbar class="py-3 px-4 px-md-5"
                  toggleable="sm"
        >
            <b-navbar-brand class="header-logo cursor" href="#" :aria-label="$t('처음으로')" @click.prevent="restart">
                <b-img class="w-100" :src="require('@shared/assets/img/common/QuantifiedESG_Logo_v3_Basic.png')" alt="QESG"/>
            </b-navbar-brand>

            <b-navbar-nav class="ml-auto">
                <dropdownLanguage :disabled="register_loading"/>
            </b-navbar-nav>
        </b-navbar>
        <b-container class="bgray-100 py-3 my-auto" fluid>
            <b-row align-h="center" class="mb-3">
                <b-col cols md="8" xl="6">
                    <div class="text-center">
                        <h2 class="fw-600 mb-0"> {{ $t('공급망 ESG 실사') }} </h2>
                    </div>
                </b-col>
            </b-row>

            <b-row align-h="center" align-v="end" class="mb-4">
                <b-col cols md="8" xl="6">
                    <b-row align-v="end">
                        <b-col v-for="step in steps" :key="step.no" cols="4" md>
                            <div class="pb-1"
                                 :class="current_step === step.no
                                     ? 'border-bottom border-color-gray-700 border-width-2 fw-600 f-120 gray-900'
                                     : 'border-bottom border-color-gray-700 f-105 gray-600'">
                                <div class="f-90 mb-0">Step {{ step.no }}.</div>
                                <div>{{ $t(step.label) }}</div>
                            </div>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <b-row align-v="end" align-h="center">
                <b-col cols md="8" xl="6">
                    <b-card class="px-lg-3 px-lg-4 py-3 shadow-sm">
                        <!-- 테넌트 설정(원청사 목록 등)이 오기 전에 Step 1 이 초기화되면 ?ivpc 프리셀렉트가 틀어진다 — 도착(실패 포함)까지 기다린다 -->
                        <div v-if="!config_ready" class="text-center py-5">
                            <b-spinner small/>
                        </div>
                        <DuplicatedRequestInfo v-else-if="duplicated"/>
                        <template v-else>
                            <RegisterStep1 v-if="register_step === 1"/>
                            <RegisterStep2 v-else-if="register_step === 2"/>
                            <RegisterStep3 v-else-if="register_step === 3"/>
                        </template>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>

        <RegisterInquiryModal/>
        <global-footer/>
    </main>
</template>

<script>
import GlobalFooter from '@shared/components/common/GlobalFooter.vue';
import dropdownLanguage from '@shared/components/utils/dropdown-language.vue';
import RegisterStep1 from '@/components/auth/Register/_SubComponents/RegisterStep1/RegisterStep1.vue';
import {mapActions, mapState} from 'vuex';
import {resolveLocale} from '@/i18n';
import RegisterStep2 from '@/components/auth/Register/_SubComponents/RegisterStep2/RegisterStep2.vue';
import RegisterStep3 from '@/components/auth/Register/_SubComponents/RegisterStep3.vue';
import RegisterInquiryModal from '@/components/auth/Register/_SubComponents/RegisterInquiryModal.vue';
import DuplicatedRequestInfo from '@/components/auth/Register/_SubComponents/DuplicatedRequestInfo.vue';

export default {
    name: 'register',
    components: {
        DuplicatedRequestInfo,
        RegisterInquiryModal,
        RegisterStep3,
        RegisterStep2,
        RegisterStep1,
        GlobalFooter,
        dropdownLanguage,
    },
    data() {
        return {
            steps: [
                {no: 1, label: '기업 조회'},
                {no: 2, label: '정보 입력'},
                {no: 3, label: '신청완료'},
            ],
            // 로고·하단 뒤로가기에서 이미 승인한 이동 — 이어지는 라우트 가드 1회가 다시 묻지 않게 한다
            leave_approved: false,
        };
    },
    computed: {
        ...mapState('register', ['register_step', 'register_loading', 'duplicated', 'selected_vendor', 'form_dirty', 'remote_register_config']),
        config_ready() {
            return this.remote_register_config !== undefined;
        },
        // Step 2 에 입력한 값이 있으면 떠나기 전에 묻는다(새로고침·뒤로가기·하단 뒤로가기·로고·다른 화면 이동)
        needs_leave_confirm() {
            return this.register_step === 2 && !this.duplicated && this.form_dirty;
        },
        // 중복 신청 안내는 Step 2(이메일 확인)에서 나오므로 Step 2 에 불을 켠다
        current_step() {
            return this.duplicated ? 2 : this.register_step;
        },
    },
    // 하단 '뒤로가기'(CommonRegisterFooter)가 같은 확인창을 쓴다
    provide() {
        return {confirmDiscard: this.confirmDiscard};
    },
    watch: {
        // 단계는 ?step=2 로 URL 에 올린다(1 은 query 없음, 3·중복 안내는 올리지 않음). 입력값은 올리지 않는다.
        // 처음으로 돌아가면(뒤로가기 버튼·로고) URL 의 step 도 지운다
        register_step(step) {
            if (step === 1 && this.$route.query.step) this.replaceStep(undefined);
            // 휴대폰에서 아래쪽 [다음]을 눌러 넘어가면 새 단계의 안내가 화면 밖에 남는다
            window.scrollTo(0, 0);
        },
        duplicated(value) {
            if (value) window.scrollTo(0, 0);
        },
        // 승인 뒤 새로 입력하면 그 승인은 더 이상 유효하지 않다
        form_dirty(value) {
            if (value) this.leave_approved = false;
        },
        // 브라우저 뒤로가기로 step 이 사라지면 화면도 Step 1 로, 앞으로가기로 돌아오면 고른 업체가 있을 때만 Step 2 로
        '$route.query.step'(step) {
            if (step !== '2' && this.register_step !== 1) {
                this.initiateAllData();
            } else if (step === '2' && this.register_step === 1) {
                if (this.selected_vendor) this.setRegisterStep(2);
                else this.replaceStep(undefined);
            }
        },
    },
    async created() {
        // 테넌트별 register 런타임 설정 (brands DB) — 실패해도 코드 기본값으로 동작
        this.$store.dispatch('register/loadTenantRegisterConfig');

        // ?l= → 저장된 언어 → 기본 순서 (앱 부팅과 같은 규칙: i18n.js resolveLocale). 확인창 문구도 이 언어로 뜨도록 먼저 적용한다
        this.$i18n.locale = resolveLocale(this.$route.query.l, this.$i18n.availableLocales);
        document.documentElement.lang = this.$i18n.locale;

        if (this.$store.state.accessToken) {
            const {user_name, e_mail} = this.$store.state;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('로그아웃 후 새 신청'),
                message: this.$t('로그아웃 후 새 신청 안내', {user_name, e_mail})
            });
            if (!ok) {
                this.$router.replace('/');
                return;
            }
            await this.$store.dispatch('logout');
        }
    },
    mounted() {
        this.initiateAllData();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        this.initiateAllData();
    },
    // 브라우저 뒤로가기로 step=2 가 빠질 때만 묻는다. 언어(l)·업체(ivvc) 같은 다른 query 변화는 그대로 통과
    async beforeRouteUpdate(to, from, next) {
        if (to.query.step === '2' || await this.passLeaveGuard()) next();
        else next(false);
    },
    async beforeRouteLeave(to, from, next) {
        if (await this.passLeaveGuard()) next();
        else next(false);
    },
    methods: {
        ...mapActions('register', ['initiateAllData', 'setRegisterStep']),
        onBeforeUnload(event) {
            if (!this.needs_leave_confirm) return;
            event.preventDefault();
            event.returnValue = '';
        },
        // 업체 선택·입력을 버리는 이동 전의 확인창 하나(로고·하단 뒤로가기·라우트 가드 공용)
        askDiscard() {
            return this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('처음부터 다시 시작'),
                message: this.$t('처음부터 다시 시작 안내', {
                    company_name: this.selected_vendor?.company?.company_name_dip,
                    vendor_code: this.selected_vendor?.vendor_code
                })
            });
        },
        // 로고·하단 뒤로가기용. force: 입력이 없어도 묻는다(로고 — 고른 업체가 사라지므로).
        // 승인하면 이어지는 라우트 가드 1회를 통과시킨다. dirty 는 실제로 입력이 초기화될 때(reset)만 내려간다
        // — 이동이 다른 가드에 막혀도 이후 이탈·새로고침 확인이 사라지지 않게.
        async confirmDiscard(force = false) {
            if (!force && !this.needs_leave_confirm) return true;
            const ok = await this.askDiscard();
            if (ok) this.leave_approved = true;
            return ok;
        },
        async passLeaveGuard() {
            if (this.leave_approved) {
                this.leave_approved = false;
                return true;
            }
            return !this.needs_leave_confirm || this.askDiscard();
        },
        replaceStep(step) {
            const {step: _removed, ...query} = this.$route.query;
            this.$router.replace({query: step ? {...query, step} : query});
        },
        // 로고: 처음부터 다시. 서버 요청이 나가 있는 동안은 결과가 엉뚱한 화면에 반영되므로 막는다.
        // 업체를 고르고 입력 중인 Step 2 에서만 확인한다(입력값·선택이 사라지므로)
        async restart() {
            if (this.register_loading) return;
            if (this.register_step === 2 && !this.duplicated && this.selected_vendor) {
                const ok = await this.confirmDiscard(true);
                if (!ok || this.register_loading) return;
            }
            // ivvc 가 남으면 Step 1 이 같은 업체를 다시 자동 선택한다(중복 안내·Step 3 에서도 동일)
            const {step: _step, ivvc: _ivvc, ...query} = this.$route.query;
            await this.$router.replace({query}).catch(() => {});
            this.initiateAllData();
        },
    }
};

</script>

<style>
.header-logo:focus-visible {
    outline: 2px solid #0d6efd;
    outline-offset: 2px;
}

#vendor-table .b-table-row-selected.table-active {
    background-color: #b9deff38 !important;
}

.not-scroll {
    overflow: hidden;
    width: 100%;
    height: 100vh;
    touch-action: none;
}
</style>
